import { useEffect } from "react";
import echo from "@/lib/echo";
import { APP_CONSTANTS } from "@/config";
import { useAuthStore } from "@/stores/use-auth-store";
import { useHitlStore } from "@/stores/use-hitl-store";
import {apiRowToHitl,
  createdEventToRow,
  hydrateHitlRequests,
  HITL_REALTIME_ERROR,
  resolvedEventRequestId,
} from "./hitl-api";

/**
 * Subscribes once to `tenant.${tenantId}.hitl` (rewritten by Echo to the
 * canonical hyphen-form `private-tenant-{tid}-hitl`) and forwards events
 * into the store. Also performs an initial fetch of pending requests so
 * users who log in AFTER a HITL has been created still see it.
 *
 * Backend: WS-3.A broadcasts on the private channel with events
 *   .hitl.request.created / .resolved / .expired
 * Initial fetch endpoint: GET /api/v1/session/hitl/requests?state=pending
 * (session route: the tenant is the authenticated user's tenant, W0-05).
 *
 * `connected` means the approvals path is live: the initial load answered and
 * the channel is subscribed. Both failures write `error` so the inbox and the
 * bell can say so instead of showing an empty list.
 *
 * Mount this once at the top of the app tree.
 */
export function HitlPusherBridge() {
  const tenantId =
    typeof window !== "undefined"
      ? localStorage.getItem(APP_CONSTANTS.COMPANY_LOCAL_STORAGE_KEY)
      : null;
  const accessToken = useAuthStore((s) => s.accessToken);
  const upsert = useHitlStore((s) => s.upsert);
  const remove = useHitlStore((s) => s.remove);
  const setConnected = useHitlStore((s) => s.setConnected);
  const setRealtimeDisabled = useHitlStore((s) => s.setRealtimeDisabled);
  const setError = useHitlStore((s) => s.setError);

  // Initial fetch: hydrate the store with anything pending server-side.
  // Without this, the inbox is empty until the next live broadcast.
  useEffect(() => {
    if (!tenantId || !accessToken) return;
    void hydrateHitlRequests(tenantId).then(() => {
      // hydrate reports that the LIST loaded, and says so with
      // setConnected(true). With no realtime client there is nothing live
      // behind that list, and hydrate resolves after the subscribe effect
      // below, so the honest state has to be written again here or the fetch
      // would paint over it.
      //
      // This is NOT an error (2026-09-11): the list above is accurate as of
      // now, only live updates are missing. Writing HITL_REALTIME_ERROR here
      // made the inbox and the bell announce an outage on a build that was
      // simply deployed without the Pusher build args. The flag is set only
      // after hydrate resolves, so it also means "the list has loaded".
      if (!echo) {
        setConnected(false);
        setRealtimeDisabled(true);
      }
    });
  }, [tenantId, accessToken, setConnected, setRealtimeDisabled]);

  useEffect(() => {
    if (!tenantId || !accessToken) return;

    if (!echo) {
      // The build carries no Pusher configuration (lib/echo.ts), so no
      // approval can ever arrive live. The consumers say so once the initial
      // fetch above has resolved (`realtimeDisabled`); until then this is
      // still the ordinary "not connected yet" state.
      setConnected(false);
      return;
    }

    const channel = echo.private(`tenant.${tenantId}.hitl`);

    const onCreated = (payload: unknown) => {
      // Laravel broadcasts the ADR envelope, whose request sits under
      // `card.payload`; createdEventToRow also accepts a bare inbox row.
      const row = createdEventToRow(payload);
      if (!row) {
        // A request the clinic owns arrived and could not be read. Say so —
        // dropping it would hide a decision someone is waiting on.
        console.error("[HitlPusherBridge] unreadable hitl.request.created payload");
        setError("Uma aprovação chegou e não pôde ser exibida. Atualize a página.");
        return;
      }
      upsert(apiRowToHitl(row, tenantId));
    };
    // Only a terminal decision removes a pending card. Timeout/escalation
    // events keep policy proposals pending and do not change this store.
    // Their durable notices are delivered through the notification bell.
    const onResolved = (payload: unknown) => {
      const id = resolvedEventRequestId(payload);
      if (id) remove(id);
    };
    const onExpired = onResolved;

    try {
      channel.listen(".hitl.request.created", onCreated);
      channel.listen(".hitl.decision.made", onResolved);
      channel.listen(".hitl.request.resolved", onResolved);
      channel.listen(".hitl.request.expired", onExpired);
      if (typeof channel.error === "function") {
        channel.error(() => {
          setConnected(false);
          setError(HITL_REALTIME_ERROR);
        });
      }
      setConnected(true);
    } catch (err) {
      console.error("[HitlPusherBridge] subscription failed:", err);
      setConnected(false);
      setError(HITL_REALTIME_ERROR);
    }

    return () => {
      channel.stopListening(".hitl.request.created");
      channel.stopListening(".hitl.decision.made");
      channel.stopListening(".hitl.request.resolved");
      channel.stopListening(".hitl.request.expired");
      setConnected(false);
    };
  }, [tenantId, accessToken, upsert, remove, setConnected, setError]);

  return null;
}
